import { Button } from '@/components/ui/button';
import { MorphIcon } from '@/components/ui/morph-icon';
import type { ProjectedHumanInteraction } from '@web/lib/projector';
import { ShieldAlert } from 'lucide';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  interaction: ProjectedHumanInteraction;
  disabled?: boolean;
  pending?: boolean;
  retryAvailable?: boolean;
  terminalProblem?: string | null;
  expired?: boolean;
  onDecision: (decision: Record<string, unknown>) => void;
  onRetry?: () => void;
  onReconcile?: () => void;
  onDismiss?: () => void;
};

export function approvalRuleMatcherDetails(
  value: unknown
): Array<{ label: string; value: string }> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const matcher = value as Record<string, unknown>;
  const details: Array<{ label: string; value: string }> = [];
  const action = matcher.action_pattern ?? matcher.action;
  if (typeof action === 'string' && action) {
    details.push({ label: 'Action', value: action });
  }
  if (
    typeof matcher.resource_pattern === 'string' &&
    matcher.resource_pattern
  ) {
    details.push({ label: 'Resource', value: matcher.resource_pattern });
  }
  if (typeof matcher.matcher_kind === 'string' && matcher.matcher_kind) {
    details.push({ label: 'Match type', value: matcher.matcher_kind });
  }
  if (!details.length) {
    try {
      details.push({ label: 'Matcher', value: JSON.stringify(matcher) });
    } catch {
      details.push({ label: 'Matcher', value: 'Unsupported matcher shape' });
    }
  }
  return details;
}

export function RemoteHumanInteractionCard({
  interaction,
  disabled = false,
  pending = false,
  retryAvailable = false,
  terminalProblem = null,
  expired = false,
  onDecision,
  onRetry,
  onReconcile,
  onDismiss,
}: Props) {
  const request = interaction.request;
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    setValues({});
  }, [interaction.interactionId]);
  const title = String(
    request.title ||
      (interaction.interactionType === 'approval'
        ? 'Approval required'
        : 'Input required')
  );
  const question = String(request.question || request.content || '');
  const scopes = Array.isArray(request.allowed_scopes)
    ? request.allowed_scopes.map(String)
    : ['once'];
  const action =
    request.action && typeof request.action === 'object'
      ? (request.action as Record<string, unknown>)
      : null;
  const targets = useMemo(
    () =>
      Array.isArray(action?.target_resources)
        ? action.target_resources.map(String).filter(Boolean)
        : [],
    [action]
  );
  const fields = Array.isArray(request.fields)
    ? (request.fields as Array<Record<string, unknown>>)
    : [];
  const matcher =
    request.rule_matcher && typeof request.rule_matcher === 'object'
      ? (request.rule_matcher as Record<string, unknown>)
      : null;
  const matcherDetails = useMemo(
    () => approvalRuleMatcherDetails(matcher),
    [matcher]
  );
  const persistentMatcherVisible = Boolean(
    matcher &&
    matcher.matcher_kind === 'exact_literal' &&
    typeof (matcher.action_pattern ?? matcher.action) === 'string' &&
    typeof matcher.resource_pattern === 'string' &&
    matcher.resource_pattern.length > 0
  );
  const displayArguments =
    request.display_arguments && typeof request.display_arguments === 'object'
      ? request.display_arguments
      : null;
  const allowedScopes = persistentMatcherVisible
    ? scopes
    : scopes.filter((scope) => scope === 'once');

  return (
    <section className="mb-2 rounded-2xl border border-solid border-ds-border-warning-subtle-default bg-ds-bg-warning-subtle-default p-4">
      <div className="flex items-start gap-3">
        <MorphIcon
          icon={ShieldAlert}
          className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-warning-default-default"
        />
        <div className="min-w-0 flex-1">
          <p className="text-body-sm font-semibold text-ds-text-neutral-default-default">
            {title}
          </p>
          {question ? (
            <p className="mt-1 text-body-sm text-ds-text-neutral-muted-default">
              {question}
            </p>
          ) : null}
          {action?.operation || targets.length ? (
            <div className="mt-3 rounded-xl bg-ds-bg-neutral-default-default px-3 py-2 text-label-xs text-ds-text-neutral-muted-default">
              {action?.operation ? <div>{String(action.operation)}</div> : null}
              {targets.slice(0, 3).map((target) => (
                <div key={target} className="truncate font-mono" title={target}>
                  {target}
                </div>
              ))}
            </div>
          ) : null}
          {matcher ? (
            <div className="mt-2 rounded-xl bg-ds-bg-neutral-default-default px-3 py-2 text-label-xs text-ds-text-neutral-muted-default">
              <div>Persistent rule matcher</div>
              {matcherDetails.map((detail) => (
                <div
                  key={detail.label}
                  className="truncate font-mono"
                  title={detail.value}
                >
                  {detail.label.toLowerCase()}={detail.value}
                </div>
              ))}
            </div>
          ) : null}
          {displayArguments ? (
            <details className="mt-2 rounded-xl bg-ds-bg-neutral-default-default px-3 py-2 text-label-xs text-ds-text-neutral-muted-default">
              <summary className="cursor-pointer font-medium">
                Review arguments (secrets redacted)
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono">
                {JSON.stringify(displayArguments, null, 2)}
              </pre>
            </details>
          ) : null}
          {expired ? (
            <p className="mt-3 text-label-xs text-ds-text-warning-default-default">
              This request expired before a durable decision was recorded. It is
              kept here for history and no longer blocks the task input.
            </p>
          ) : null}
          {pending ? (
            <p className="mt-3 text-label-xs text-ds-text-neutral-muted-default">
              Decision sent. Waiting for the Desktop to durably record it…
            </p>
          ) : null}
          {retryAvailable ? (
            <p className="mt-3 text-label-xs text-ds-text-warning-default-default">
              Desktop confirmation is delayed. Retry sends the exact same
              request id, payload, and expected version; it does not create a
              new decision.
            </p>
          ) : null}
          {terminalProblem ? (
            <p className="mt-3 text-label-xs text-ds-text-warning-default-default">
              The saved decision reached terminal state “{terminalProblem}”.
              Refresh durable status, or submit a replacement below. A
              replacement explicitly supersedes this unsuccessful command and
              remains protected by the interaction version check.
            </p>
          ) : null}
          {!expired && interaction.interactionType === 'form' ? (
            <div className="mt-3 space-y-2">
              {fields.map((field) => {
                const id = String(field.id || field.name || '');
                if (!id) return null;
                return (
                  <label key={id} className="block text-label-xs">
                    <span>{String(field.label || id)}</span>
                    <input
                      className="border-ds-border-neutral-default mt-1 w-full rounded-xl border border-solid bg-ds-bg-neutral-default-default px-3 py-2 text-body-sm"
                      type={String(field.type || 'text')}
                      value={values[id] || ''}
                      disabled={disabled}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [id]: event.target.value,
                        }))
                      }
                    />
                  </label>
                );
              })}
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {terminalProblem && onReconcile ? (
              <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={onReconcile}
              >
                Refresh durable status
              </Button>
            ) : null}
            {retryAvailable && onRetry ? (
              <Button size="sm" disabled={disabled} onClick={onRetry}>
                Retry exact decision
              </Button>
            ) : expired && onDismiss ? (
              <Button size="sm" variant="outline" onClick={onDismiss}>
                Dismiss expired request
              </Button>
            ) : expired ? null : interaction.interactionType === 'approval' ? (
              <>
                <Button
                  size="sm"
                  disabled={disabled}
                  onClick={() =>
                    onDecision({ decision: 'approved', scope: 'once' })
                  }
                >
                  Approve once
                </Button>
                {allowedScopes.includes('run') ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    onClick={() =>
                      onDecision({ decision: 'approved', scope: 'run' })
                    }
                  >
                    Allow for this Run
                  </Button>
                ) : null}
                {allowedScopes.includes('space') ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    onClick={() =>
                      onDecision({ decision: 'approved', scope: 'space' })
                    }
                  >
                    Always allow in Space
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() =>
                    onDecision({ decision: 'rejected', scope: 'once' })
                  }
                >
                  Reject
                </Button>
              </>
            ) : interaction.interactionType === 'choice' ? (
              interaction.options.map((option) => (
                <Button
                  key={String(option.option_id || option.id || option.label)}
                  size="sm"
                  variant="outline"
                  disabled={disabled}
                  onClick={() =>
                    onDecision({
                      option_id: option.option_id || option.id,
                      value: option.value,
                    })
                  }
                >
                  {String(option.label)}
                </Button>
              ))
            ) : interaction.interactionType === 'form' ? (
              <Button
                size="sm"
                disabled={disabled}
                onClick={() => onDecision({ values })}
              >
                Submit
              </Button>
            ) : interaction.interactionType !== 'question' ? (
              <>
                <Button
                  size="sm"
                  disabled={disabled}
                  onClick={() => onDecision({ decision: 'approved' })}
                >
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => onDecision({ decision: 'rejected' })}
                >
                  Reject
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
